import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import FDRPill from "@/components/app/FDRPill";
import { T } from "@/lib/testIds";
import { ChevronLeft, ArrowLeftRight } from "lucide-react";

const StatRow = ({ label, value, sub }) => (
  <div className="flex justify-between items-baseline py-2 border-b border-totem-borderSoft last:border-0">
    <span className="mono uppercase text-[11px] text-totem-textDim tracking-wider">{label}</span>
    <span className="mono">
      {value}
      {sub && <span className="text-totem-textDim text-xs ml-1">{sub}</span>}
    </span>
  </div>
);

export default function PlayerDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["player", id],
    queryFn: async () => (await api.get(`/players/${id}`)).data,
    staleTime: 60_000,
  });

  if (isLoading) return <div className="totem-card h-64 animate-pulse mt-4" />;
  if (error || !data)
    return <div className="totem-card p-6 text-totem-red">Player not found.</div>;

  const p = data.player;
  const chartData = (data.history || []).map((h) => ({
    gw: `GW${h.round}`,
    pts: h.total_points,
    xg: parseFloat(h.expected_goals || 0),
    xa: parseFloat(h.expected_assists || 0),
  }));

  return (
    <div data-testid={T.playerDetail} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center justify-between">
        <Link
          to="/players"
          className="inline-flex items-center gap-1 text-totem-textDim hover:text-totem-green text-sm"
        >
          <ChevronLeft size={16} /> Back
        </Link>
        <Link
          to={`/compare?a=${p.id}`}
          className="inline-flex items-center gap-1.5 text-xs mono uppercase tracking-wider text-totem-gold bg-totem-gold/10 border border-totem-gold/40 rounded-full px-3 py-1.5 hover:bg-totem-gold/15"
        >
          <ArrowLeftRight size={12} /> Compare
        </Link>
      </div>

      <section className="totem-card p-5">
        <div className="flex items-center gap-4">
          <PlayerAvatar player={p} size={72} />
          <div className="flex-1 min-w-0">
            <div className="display-heading text-2xl truncate">{p.web_name}</div>
            <div className="mono uppercase text-xs text-totem-textDim mt-1">
              {p.first_name} {p.second_name}
            </div>
            <div className="mono uppercase text-[11px] text-totem-textDim mt-1">
              {p.team_short} • {p.position}
            </div>
          </div>
          <div className="text-right">
            <div className="display-heading text-3xl text-totem-green">£{p.now_cost.toFixed(1)}</div>
            <div className="mono uppercase text-[10px] text-totem-textDim">price</div>
          </div>
        </div>

        {p.news && (
          <div className="mt-4 border border-totem-amber/30 bg-totem-amber/10 rounded-lg px-3 py-2 text-xs text-totem-amber">
            {p.news}
            {p.chance_of_playing_this_round !== null && (
              <span className="ml-2 mono">({p.chance_of_playing_this_round}% GW)</span>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="totem-card-inner p-3 text-center">
            <div className="label-caps mb-1">Total pts</div>
            <div className="display-heading text-xl text-totem-green">{p.total_points}</div>
          </div>
          <div className="totem-card-inner p-3 text-center">
            <div className="label-caps mb-1">Form</div>
            <div className="display-heading text-xl">{p.form}</div>
          </div>
          <div className="totem-card-inner p-3 text-center">
            <div className="label-caps mb-1">TSB %</div>
            <div className="display-heading text-xl">{p.selected_by_percent}</div>
          </div>
        </div>
      </section>

      {chartData.length > 0 && (
        <section className="totem-card p-4">
          <div className="label-caps mb-2">Points by gameweek</div>
          <div data-testid={T.playerFormChart} className="h-48 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#1E2126" strokeDasharray="2 4" />
                <XAxis dataKey="gw" tick={{ fill: "#5F6772", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#22262C" }} tickLine={false} />
                <YAxis tick={{ fill: "#5F6772", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#22262C" }} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#14171C", border: "1px solid #262A30", borderRadius: 8 }}
                  labelStyle={{ color: "#E4E8EE", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  itemStyle={{ color: "#00FF85", fontFamily: "JetBrains Mono", fontSize: 11 }}
                />
                <Line type="monotone" dataKey="pts" stroke="#00FF85" strokeWidth={2.4} dot={{ r: 3, fill: "#00FF85" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <section className="totem-card p-5">
        <div className="label-caps mb-2">Underlying stats</div>
        <StatRow label="Minutes" value={p.minutes} />
        <StatRow label="Goals" value={p.goals_scored} sub={`xG ${p.expected_goals.toFixed(2)}`} />
        <StatRow label="Assists" value={p.assists} sub={`xA ${p.expected_assists.toFixed(2)}`} />
        <StatRow label="Clean sheets" value={p.clean_sheets} />
        <StatRow label="Bonus" value={p.bonus} sub={`BPS ${p.bps}`} />
        <StatRow label="ICT index" value={p.ict_index.toFixed(1)} />
        <StatRow label="Points/game" value={p.points_per_game} />
        <StatRow label="Transfers in (this GW)" value={p.transfers_in_event.toLocaleString()} />
        <StatRow label="Transfers out (this GW)" value={p.transfers_out_event.toLocaleString()} />
      </section>

      {data.upcoming?.length > 0 && (
        <section data-testid={T.playerUpcoming} className="totem-card p-5">
          <div className="label-caps mb-3">Upcoming fixtures</div>
          <div className="space-y-2">
            {data.upcoming.slice(0, 8).map((f) => (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-totem-borderSoft last:border-0">
                <div className="mono text-xs text-totem-textDim">
                  {f.event ? `GW${f.event}` : "TBC"}
                </div>
                <div className="flex items-center gap-2">
                  <span className="mono text-sm font-semibold">
                    {f.opponent_short || "?"}
                  </span>
                  <span className="mono uppercase text-[10px] text-totem-textDim">
                    ({f.is_home ? "H" : "A"})
                  </span>
                </div>
                <FDRPill
                  difficulty={f.difficulty}
                  label={`FDR ${f.difficulty ?? "-"}`}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {data.history_past?.length > 0 && (
        <section className="totem-card p-5">
          <div className="label-caps mb-3">Prior seasons</div>
          <div className="space-y-2">
            {data.history_past.slice(-5).map((h) => (
              <div key={h.season_name} className="flex justify-between text-sm mono">
                <span className="text-totem-textDim">{h.season_name}</span>
                <span>
                  {h.total_points} pts • £{(h.end_cost / 10).toFixed(1)}m
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
